import React, { useEffect, useRef, useState, useMemo } from "react";
import { Badge } from "reactstrap";
import api from "../../../utils/api";

const ActiveOrderSlider = ({ order, menuItems = [], refreshOrder, tenant }) => {
  const [timers, setTimers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const sliderRef = useRef();

  // ⏱ FORMAT TIME
  const formatTime = (sec) => {
    if (sec === null || sec === undefined) return "";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  // 🔥 GROUP ITEMS
  const groupedItems = useMemo(() => {
    if (!Array.isArray(order?.items)) return [];

    const map = {};

    order.items.forEach((item, index) => {
      const key = item.menuItemId || item.name;

      if (!map[key]) {
        map[key] = {
          ...item,
          quantity: 0,
          indexes: [],
          statuses: []
        };
      }

      const qty = item.quantity || 1;

      map[key].quantity += qty;
      map[key].indexes.push(index);

      for (let i = 0; i < qty; i++) {
        map[key].statuses.push(item.status);
      }
    });

    return Object.values(map);
  }, [order]);

  // ⏱ TIMER
  useEffect(() => {
    if (!order?.items) return;

    const interval = setInterval(() => {
      const updated = {};
      let storedTimes =
        JSON.parse(localStorage.getItem("prepStartTimes")) || {};

      order.items.forEach((item, index) => {
        if (item.status !== "Preparing") {
          updated[index] = null;
          return;
        }

        const key = `${order.orderId}_${index}`;

        const menuItem = menuItems.find(
          (m) => String(m.id) === String(item.menuItemId)
        );

        const prepMinutes =
          item.preparationTime || menuItem?.prepTime || 10;

        const prepSeconds = prepMinutes * 60;

        if (!storedTimes[key]) {
          storedTimes[key] = new Date().toISOString();
        }

        const startTime = new Date(storedTimes[key]);
        const elapsed = Math.floor((new Date() - startTime) / 1000);
        const remaining = prepSeconds - elapsed;

        updated[index] = remaining > 0 ? remaining : 0;
      });

      localStorage.setItem(
        "prepStartTimes",
        JSON.stringify(storedTimes)
      );

      setTimers(updated);
    }, 1000);

    return () => clearInterval(interval);
  }, [order?.items, menuItems]);

  // 🎯 AUTO SLIDE
  useEffect(() => {
    if (!groupedItems.length) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) =>
        prev === groupedItems.length - 1 ? 0 : prev + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [groupedItems]);

  // 🎯 SCROLL
  useEffect(() => {
    if (!sliderRef.current) return;

    const width = sliderRef.current.clientWidth;

    sliderRef.current.scrollTo({
      left: currentIndex * width,
      behavior: "smooth"
    });
  }, [currentIndex]);

  const normalize = (s) => s?.toString().trim().toLowerCase();

  const getFinalStatus = (statuses, isBarItem) => {
    const normalized = statuses.map(normalize);

    if (isBarItem) {
      if (normalized.some((s) => s === "delivered")) return "Delivered";
      if (normalized.some((s) => s === "preparing")) return "Preparing";
      return "Waiting";
    }

    if (normalized.every((s) => s === "delivered")) return "Delivered";
    if (normalized.some((s) => s === "preparing")) return "Preparing";
    return "Waiting";
  };

  // 🔁 REPEAT ORDER (FIXED TENANT)
  const handleRepeatOrder = async (item) => {
    try {
      const payload = {
        items: [
          {
            menuItemId: item.menuItemId,
            quantity: 1
          }
        ]
      };

      const orderId = order?.orderId || order?._id;

      if (!orderId) return;

      await api.post(
        `/guest/orders/${orderId}/add-items`,
        payload,
        {
          params: { tenant } // ✅ FIXED
        }
      );

      if (refreshOrder) refreshOrder();

    } catch (err) {
      console.error("Repeat failed", err);
    }
  };

  if (!groupedItems.length) return null;

  return (
    <div style={{ width: "100%", overflow: "hidden" }}>
      <div ref={sliderRef} style={{ display: "flex", overflow: "hidden" }}>
        {groupedItems.map((item, i) => {

          const isBarItem =
            item.menuGroup?.toLowerCase() === "bar" ||
            item.categoryModel?.toLowerCase()?.includes("bar");

          const finalStatus = getFinalStatus(item.statuses, isBarItem);

          const itemTimers = item.indexes
            .map((idx) => timers[idx])
            .filter((t) => t !== null);

          const maxTimer =
            itemTimers.length > 0 ? Math.max(...itemTimers) : null;

          return (
            <div
              key={i}
              style={{
                minWidth: "100%",
                padding: "10px 16px",
                boxSizing: "border-box"
              }}
            >
              <div
                style={{
                  background: "#fff",
                  borderRadius: 20,
                  padding: 14,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  border: "1px solid #eee"
                }}
              >
                <img
                  src={item.image}
                  alt=""
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 14,
                    objectFit: "cover"
                  }}
                />

                <div style={{ flex: 1, marginLeft: 12 }}>

                  {/* ✅ FIXED INLINE NAME + QTY */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontWeight: 600,
                      flexWrap: "nowrap",
                      overflow: "hidden"
                    }}
                  >
                    <div
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "65%"
                      }}
                    >
                      {item.name}
                    </div>

                    <Badge
                      color="primary"
                      style={{
                        flexShrink: 0,
                        whiteSpace: "nowrap"
                      }}
                    >
                      Qty: {String(item.quantity).padStart(2, "0")}
                    </Badge>
                  </div>

                  <div style={{ marginTop: 4 }}>
                    <Badge
                      color={
                        finalStatus === "Waiting"
                          ? "secondary"
                          : finalStatus === "Preparing"
                          ? "warning"
                          : "success"
                      }
                    >
                      {finalStatus}
                    </Badge>
                  </div>
                </div>

                <div style={{ minWidth: 90, textAlign: "right" }}>
                  {isBarItem ? (
                    finalStatus === "Delivered" && (
                      <button
                        onClick={() => handleRepeatOrder(item)}
                        style={{
                          border: "none",
                          background: "#687cfe",
                          color: "#fff",
                          borderRadius: 14,
                          padding: "6px 12px",
                          fontSize: 13,
                          fontWeight: 600
                        }}
                      >
                        Repeat
                      </button>
                    )
                  ) : (
                    finalStatus === "Preparing" &&
                    maxTimer !== null && (
                      <div style={{ fontSize: 20, fontWeight: 600 }}>
                        {formatTime(maxTimer)}
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActiveOrderSlider;
